import { useState } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";

export default function TrialBanner({ daysLeft, totalDays, trialEnd, onUpgrade }) {
  const [dismissed, setDismissed] = useState(false);
  const isMobile = useIsMobile();

  if (dismissed || daysLeft <= 0) return null;

  const pct      = Math.round(((totalDays - daysLeft) / totalDays) * 100);
  const urgent   = daysLeft <= 3;
  const barColor = urgent ? "#e07070" : "#D4AF37";
  const bgFrom   = urgent ? "rgba(180,60,60,0.12)" : "rgba(212,175,55,0.1)";
  const bgTo     = urgent ? "rgba(180,60,60,0.04)" : "rgba(212,175,55,0.03)";
  const bdColor  = urgent ? "rgba(180,60,60,0.25)"  : "rgba(212,175,55,0.2)";
  const textColor = urgent ? "#e07070" : "#D4AF37";

  // ── Mobile layout: two compact rows ──────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{
        background: `linear-gradient(90deg, ${bgFrom}, ${bgTo})`,
        borderBottom: `1px solid ${bdColor}`,
        padding: "8px 16px",
        flexShrink: 0,
      }}>
        {/* Row 1: icon + label + days text + dismiss */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span style={{ fontSize: "12px" }}>{urgent ? "⏰" : "✦"}</span>
            <span style={{
              fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
              color: textColor, fontWeight: "600",
            }}>
              {urgent ? `${daysLeft}d left!` : "Free Trial"}
            </span>
            <span style={{
              fontSize: "10px", color: "var(--text-secondary)",
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              · {daysLeft}/{totalDays} days · ends {trialEnd}
            </span>
          </div>
          {/* Dismiss */}
          <button
            onClick={() => setDismissed(true)}
            style={{
              background: "none", border: "none", color: "var(--text-faint)",
              fontSize: "16px", cursor: "pointer", padding: "0 2px",
              lineHeight: 1, flexShrink: 0,
            }}
            aria-label="Dismiss"
          >×</button>
        </div>

        {/* Row 2: progress bar + upgrade button */}
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {/* Progress bar */}
          <div style={{ flex: 1, height: "3px", background: "var(--bg-hover)", borderRadius: "2px" }}>
            <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: "2px", transition: "width 0.4s" }} />
          </div>
          {/* Upgrade button */}
          <button
            onClick={onUpgrade}
            style={{
              padding: "4px 12px", borderRadius: "12px",
              background: urgent ? "rgba(224,112,112,0.15)" : "rgba(212,175,55,0.12)",
              border: `1px solid ${urgent ? "rgba(224,112,112,0.4)" : "rgba(212,175,55,0.35)"}`,
              color: textColor,
              fontSize: "10px", fontFamily: "'JetBrains Mono', monospace",
              cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            }}
          >
            Upgrade →
          </button>
        </div>
      </div>
    );
  }

  // ── Desktop layout: single row ────────────────────────────────────────────
  return (
    <div style={{
      background: `linear-gradient(90deg, ${bgFrom}, ${bgTo})`,
      borderBottom: `1px solid ${bdColor}`,
      padding: "10px 32px",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "16px", flex: 1, minWidth: 0 }}>

        {/* Icon + label */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
          <span style={{ fontSize: "14px" }}>{urgent ? "⏰" : "✦"}</span>
          <span style={{
            fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
            color: textColor, fontWeight: "500",
          }}>
            {urgent ? `Only ${daysLeft} day${daysLeft === 1 ? "" : "s"} left!` : `Free Trial`}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ flex: 1, maxWidth: "160px", height: "3px", background: "var(--bg-hover)", borderRadius: "2px" }}>
          <div style={{ width: `${pct}%`, height: "100%", background: barColor, borderRadius: "2px", transition: "width 0.4s" }} />
        </div>

        {/* Days text */}
        <span style={{ fontSize: "11px", color: "var(--text-secondary)", fontFamily: "'JetBrains Mono', monospace", flexShrink: 0 }}>
          {daysLeft} of {totalDays} days remaining · trial ends {trialEnd}
        </span>
      </div>

      {/* Upgrade button + dismiss */}
      <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
        <button
          onClick={onUpgrade}
          style={{
            padding: "6px 16px", borderRadius: "14px",
            background: urgent ? "rgba(224,112,112,0.15)" : "rgba(212,175,55,0.12)",
            border: `1px solid ${urgent ? "rgba(224,112,112,0.4)" : "rgba(212,175,55,0.35)"}`,
            color: textColor,
            fontSize: "11px", fontFamily: "'JetBrains Mono', monospace",
            cursor: "pointer", transition: "background 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = urgent ? "rgba(224,112,112,0.25)" : "rgba(212,175,55,0.22)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = urgent ? "rgba(224,112,112,0.15)" : "rgba(212,175,55,0.12)"; }}
        >
          Upgrade to Premium →
        </button>

        <button
          onClick={() => setDismissed(true)}
          style={{
            background: "none", border: "none", color: "var(--text-faint)", fontSize: "14px",
            cursor: "pointer", padding: "2px 4px", lineHeight: 1,
            transition: "color 0.15s",
          }}
          onMouseEnter={e => { e.currentTarget.style.color = "var(--text-muted)"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "var(--text-faint)"; }}
          title="Dismiss"
        >×</button>
      </div>
    </div>
  );
}
